import React, { ChangeEvent, useState } from 'react';

import FormFieldError from './FormFieldError';
import FormFieldLabel from './FormFieldLabel';
import FormFieldTooltip, { TooltipSource } from './FormFieldTooltip';
import useFormField, { FieldEvent, FieldWidth } from './useFormField';

interface IProps {
  name: string;
  label?: string;
  tooltip?: TooltipSource;
  description?: string;
  placeholder?: string;
  disabled?: boolean;
  type?: 'text' | 'email';
  width?: FieldWidth;
  autoFocus?: boolean;
  formatter?: (value: string) => string;
  onFocus?: FieldEvent<string>;
  onChange?: FieldEvent<string>;
  onBlur?: FieldEvent<string>;
}

function FormTextField({
  name,
  label,
  tooltip,
  description,
  placeholder,
  disabled,
  type,
  width,
  formatter,
  onFocus,
  onChange,
  onBlur,
  autoFocus = false,
}: IProps) {
  const [isFocus, setFocus] = useState(undefined);
  const {
    id,
    fieldProps,
    inputProps,
    setFieldValue,
    setFieldTouched,
    required,
    disabled: formDisabled,
  } = useFormField<string>(name, width, { onFocus, onChange, onBlur }, autoFocus);

  const getFormattedValue = (value: string): string => {
    value = value || '';

    if (!isFocus && formatter) {
      return formatter(value);
    }
    return value;
  };

  const onFocusHandler = () => {
    setFocus(true);
    inputProps.onFocus();
  };

  const onChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFieldValue(name, value);
  };

  const onBlurHandler = () => {
    setFocus(false);
    setFieldTouched(name, true);
  };

  return (
    <div {...fieldProps}>
      {label && (
        <div className="novoFormField__name">
          <FormFieldLabel for={id} required={required}>
            {label}
          </FormFieldLabel>
          <FormFieldTooltip source={tooltip} />
        </div>
      )}
      {description && <p className="novoFormField__description">{description}</p>}
      <input
        className="novoFormField__input"
        id={id}
        type={type}
        placeholder={placeholder}
        {...inputProps}
        onFocus={onFocusHandler}
        onChange={onChangeHandler}
        onBlur={onBlurHandler}
        required={required}
        disabled={formDisabled || disabled}
        value={getFormattedValue(inputProps.value)}
      />
      <FormFieldError name={name} />
    </div>
  );
}

export default FormTextField;
